import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AppShell from "@/components/AppShell";
import BiomarkerTracker from "@/components/genome/BiomarkerTracker";
import PatientReports from "@/components/genome/PatientReports";
import OpportunityExplainer from "@/components/ai/OpportunityExplainer";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Play, CheckCircle2, Compass, Info, Download } from "lucide-react";

const DOMAIN_COLOR = {
  metabolic: "#c45f4a",
  cardio: "#dc2626",
  activity: "#2563eb",
  nutrition: "#16a34a",
  recovery: "#7c3aed",
  screening: "#0b3d3a",
};

export default function GenomeMap() {
  const navigate = useNavigate();
  const [data, setData] = useState({ opportunities: [], intake: null });
  const [loading, setLoading] = useState(true);
  const [openOp, setOpenOp] = useState(null);
  const [checkin, setCheckin] = useState({ note: "", adherence: 70 });
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/genome/my/opportunities");
      setData(res);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startOp = async (op) => {
    try {
      await api.post(`/genome/opportunities/${op.id}/start`);
      toast.success("Journey started");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const postCheckin = async () => {
    if (!openOp || !checkin.note.trim()) return;
    setPosting(true);
    try {
      await api.post(`/genome/opportunities/${openOp.id}/checkin`, {
        note: checkin.note,
        adherence_pct: Number(checkin.adherence) || 0,
      });
      toast.success("Check-in saved");
      setCheckin({ note: "", adherence: 70 });
      setOpenOp(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPosting(false); }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get("/genome/my/map.pdf", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "prevention-map.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) return <AppShell pageTitle="GenomePramana · Prevention Map"><div className="py-20 text-center text-muted-foreground">Loading your map…</div></AppShell>;

  if (!data.opportunities?.length) {
    return (
      <AppShell pageTitle="GenomePramana · Prevention Map">
        <div className="max-w-xl rounded-lg border border-border bg-white p-8 text-center">
          <Compass size={28} className="text-primary mx-auto" />
          <h2 className="mt-3 font-serif text-2xl text-primary">No prevention map yet</h2>
          <p className="mt-2 text-sm text-muted-foreground">Share your biomarkers and lifestyle — we'll surface your top 5 opportunities in under a minute.</p>
          <Button className="mt-5" onClick={() => navigate("/genome/start")} data-testid="genome-start-again">Start intake</Button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      pageTitle="Your prevention map"
      pageDescription="Top 5 opportunities ranked by Impact × Modifiability × Evidence × Actionability × Readiness. Every card explains why."
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportPdf} data-testid="genome-export-pdf">
            <Download size={14} className="mr-1" /> Share with physician (PDF)
          </Button>
          <Button variant="outline" onClick={() => navigate("/genome/start")} data-testid="genome-refine">
            Update intake
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        {data.opportunities.map((op, idx) => {
          const color = DOMAIN_COLOR[op.domain] || "#0b3d3a";
          const started = op.status === "started";
          return (
            <div key={op.id} className="rounded-lg border border-border bg-white p-5" data-testid={`genome-op-${op.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest" style={{ color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} /> {op.domain}
                  </div>
                  <div className="mt-1 font-serif text-xl text-primary leading-snug">#{idx + 1} · {op.title}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-serif text-3xl text-primary leading-none">{op.composite}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Score</div>
                </div>
              </div>

              <div className="mt-3 flex items-start gap-2 rounded-md bg-muted/40 p-3">
                <Info size={13} className="text-primary mt-0.5 shrink-0" />
                <div className="text-xs text-muted-foreground leading-relaxed">{op.why}</div>
              </div>

              <div className="mt-4 grid grid-cols-5 gap-2 text-center">
                {["impact", "modifiability", "evidence", "actionability", "readiness"].map((d) => (
                  <div key={d}>
                    <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{d.slice(0, 7)}</div>
                    <div className="font-serif text-sm text-primary">{op.scores[d]}</div>
                    <Progress value={op.scores[d]} className="h-1 mt-1" />
                  </div>
                ))}
              </div>

              <div className="mt-4 rounded-md border border-dashed border-border p-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Objective</div>
                <div className="text-sm text-primary">{op.objective}</div>
                <ul className="mt-3 space-y-1.5">
                  {op.actions.slice(0, 4).map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground"><span className="text-primary">•</span>{a}</li>
                  ))}
                </ul>
                <OpportunityExplainer opportunityId={op.id} opportunityKey={op.key} />
              </div>

              <div className="mt-4 flex items-center justify-between gap-2">
                <div className="text-[11px] text-muted-foreground">
                  {started ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={12} /> Journey active</span> : "Ready to start"}
                </div>
                {started ? (
                  <Button size="sm" variant="outline" onClick={() => setOpenOp(op)} data-testid={`genome-checkin-${op.key}`}>Log check-in</Button>
                ) : (
                  <Button size="sm" onClick={() => startOp(op)} data-testid={`genome-start-${op.key}`}>
                    <Play size={12} className="mr-1" /> Start journey
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8">
        <BiomarkerTracker />
      </div>

      <div className="mt-6">
        <PatientReports />
      </div>

      <Dialog open={!!openOp} onOpenChange={(o) => { if (!o) setOpenOp(null); }}>
        <DialogContent data-testid="genome-checkin-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-primary text-xl">{openOp?.title} — weekly check-in</DialogTitle>
            <DialogDescription>How did it go this week?</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>What went well, what didn't?</Label>
              <Textarea
                value={checkin.note}
                onChange={(e) => setCheckin((p) => ({ ...p, note: e.target.value }))}
                rows={4}
                data-testid="genome-checkin-note"
              />
            </div>
            <div>
              <Label>Adherence (%)</Label>
              <Input
                type="number" min="0" max="100"
                value={checkin.adherence}
                onChange={(e) => setCheckin((p) => ({ ...p, adherence: e.target.value }))}
                data-testid="genome-checkin-adherence"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpenOp(null)}>Cancel</Button>
              <Button onClick={postCheckin} disabled={posting || !checkin.note.trim()} data-testid="genome-checkin-submit">
                {posting ? "Saving…" : "Save check-in"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
